import React, { useEffect, useMemo, useRef } from "react";
import styles from "./AIImageExpand.module.css";
import { Button, Container, Typography, Box, Grid } from "@mui/material";
import Navbar from "../../Navbar";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Footer from "../../Footer";
import FAQ from "./FAQ";
import PricingCards from "../../PricingCards";
import ImageSlider from "../../ImageSlider";
import ImageSlider2 from "../../ImageSlider2";
import DownloadSection from "../../DownloadSection";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const AIImageExpand = () => {
  const seoData = useMemo(() => ({
    // Primary Meta Tags
    title: "AI Image Expander Tool: Enlarge & Extend Photos with AI | [Your Brand]",
    description: "Transform your images with AI-powered expansion technology. Automatically extend backgrounds, upscale low-res photos, and create perfect compositions for social media, print, or digital use. Free trial available!",
    keywords: [
      "AI image expander", 
      "photo extender tool", 
      "AI outpainting software",
      "image enlargement AI", 
      "background generation tool",
      "seamless photo expansion",
      "AI photo editor online",
      "batch image processing",
      "4K image upscaler",
      "social media image resizing",
      "ecommerce product photo editor",
      "landscape extension AI"
    ].join(", "),

    // URL Structure
    canonical: "https://yourdomain.com/ai-image-expand",
    alternateUrls: [
      { hreflang: "es", href: "https://yourdomain.com/es/expansor-imagen-ai" },
      { hreflang: "fr", href: "https://yourdomain.com/fr/agrandir-image-ai" }
    ],

    // Social Meta
    openGraph: {
      ogTitle: "AI-Powered Image Expansion Tool | [Your Brand]",
      ogDescription: "Extend images seamlessly with AI magic. Perfect for social media creatives, product photography, and digital art. Try our free online editor!",
      ogImage: "https://cdn.yourdomain.com/social/ai-expand-og.jpg",
      ogVideo: "https://cdn.yourdomain.com/videos/ai-expand-demo.mp4"
    },
    
    twitterCard: {
      cardType: "summary_large_image",
      twitterSite: "@YourBrandHandle",
      twitterCreator: "@YourCreatorHandle",
      twitterImageAlt: "Before/After comparison of AI image expansion"
    },

    // Structured Data
    schemaJSON: [
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "[Your Brand] AI Image Expander",
        operatingSystem: ["Windows", "macOS", "Android", "iOS"],
        applicationCategory: ["Multimedia", "PhotoEditor"],
        screenshot: "https://cdn.yourdomain.com/interface/expand-tool-ui.jpg",
        softwareVersion: "2.1",
        downloadUrl: "https://yourdomain.com/download",
        featureList: [
          "Context-aware image expansion",
          "Smart object preservation",
          "4K/8K resolution support",
          "Batch processing (Pro version)",
          "Style transfer integration",
          "Cloud storage integration"
        ],
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          lowPrice: "0",
          highPrice: "29.99",
          offerCount: "3",
          offers: [
            {
              "@type": "Offer",
              name: "Free Plan",
              price: "0",
              description: "3 expansions/day with watermark"
            },
            {
              "@type": "Offer",
              name: "Pro Monthly",
              price: "12.99",
              subscription: "monthly"
            },
            {
              "@type": "Offer",
              name: "Pro Annual",
              price: "129.99",
              subscription: "annual"
            }
          ]
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.8",
          ratingCount: "3562",
          bestRating: "5",
          worstRating: "1",
          reviewCount: "285"
        }
      },
      {
        "@type": "HowTo",
        name: "How to Expand Images with AI",
        step: [
          {
            "@type": "HowToStep",
            text: "Upload your image or drag-and-drop into the editor"
          },
          {
            "@type": "HowToStep",
            text: "Select expansion areas using smart guides"
          },
          {
            "@type": "HowToStep",
            text: "Choose AI style parameters (landscape, portrait, abstract)"
          },
          {
            "@type": "HowToStep",
            text: "Process and download in multiple formats (JPG, PNG, TIFF)"
          }
        ]
      },
    ],

    // Performance Optimization
    preloadAssets: [
      { href: "https://cdn.yourdomain.com/css/ai-tools.min.css", as: "style" },
      { href: "https://cdn.yourdomain.com/js/ai-expand.min.js", as: "script" }
    ],

    // Security/Trust
    trustMarkers: {
      secureConnection: true,
      privacyPolicy: "https://yourdomain.com/privacy",
      dataProtection: "GDPR compliant",
      awards: ["2023 Best AI Tool Award", "PhotoEditors Choice 2024"]
    },

    // Mobile Optimization
    mobileSettings: {
      ampUrl: "https://yourdomain.com/amp/ai-image-expand",
      viewport: "width=device-width, initial-scale=1.0",
      themeColor: "#1a237e"
    },

    // Additional Metadata
    author: "[Your Brand]",
    publisher: "[Your Brand]",
    robots: "index, follow",
    revisitAfter: "7 days",
    language: "English",
    geo: {
      placeName: "San Francisco, CA",
      region: "US-CA",
      position: "37.7749;-122.4194"
    }
  }), []);
  // Refs declarations
  const sectionRefs = useRef([]);
  const gradientRef = useRef(null);
  const headingRef = useRef([]);
  const imageRef = useRef(null);
  const buttonRef = useRef(null);
  const featureRefs = useRef([]);
  const videoRefs = useRef([]);
  const sectionHeadingRef = useRef(null);
  const heroContentRef = useRef(null);
  const descriptionRef = useRef(null); // New ref for description
  const navigate=useNavigate()

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Text animations
      gsap.fromTo(
        headingRef.current,
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          stagger: 0.3,
          ease: "power4.out",
          scrollTrigger: {
            trigger: heroContentRef.current,
            start: "top 80%",
            end: "top 50%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Gradient text specific animation
      gsap.fromTo(
        headingRef.current[0],
        {
          backgroundPosition: "100% 100%",
        },
        {
          backgroundPosition: "0% 100%",
          duration: 2,
          ease: "power2.inOut",
          scrollTrigger: {
            trigger: headingRef.current[0],
            start: "top 80%",
            end: "top 30%",
            scrub: 1,
          },
        }
      );

      // Stroke text animation
      gsap.fromTo(
        headingRef.current[1],
        {
          textStrokeWidth: "0px",
          opacity: 0,
        },
        {
          textStrokeWidth: "3px",
          opacity: 1,
          duration: 1.5,
          ease: "power4.out",
          scrollTrigger: {
            trigger: headingRef.current[1],
            start: "top 85%",
            end: "top 60%",
            scrub: 1,
          },
        }
      );

      // Description animation
      gsap.fromTo(
        descriptionRef.current,
        {
          opacity: 0,
          y: 30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: descriptionRef.current,
            start: "top 90%",
            end: "top 70%",
            toggleActions: "play none none reverse",
          },
        }
      );
      // Gradient position animation
      gsap.fromTo(
        gradientRef.current,
        { backgroundPosition: "0% 0%" },
        {
          backgroundPosition: "100% 100%",
          ease: "power2.inOut",
          scrollTrigger: {
            start: "top top",
            end: "bottom bottom",
            scrub: 1.5,
          },
        }
      );

      // Color intensity animation
      gsap.fromTo(
        gradientRef.current,
        {
          backgroundImage:
            "linear-gradient(315deg, #0a0c24 0%, #1a1b3a 50%,rgb(23, 5, 24) 100%)",
        },
        {
          backgroundImage:
            "linear-gradient(315deg, #050814 0%, #0f1028 50%, #1a081b 100%)",
          ease: "power2.inOut",
          scrollTrigger: {
            start: "top center",
            end: "bottom center",
            scrub: 1,
          },
        }
      );
      // Hero section animations
      gsap.from(imageRef.current, {
        scale: 1.2,
        duration: 1.5,
        ease: "power4.out",
        transformOrigin: "center center",
      });

      const heroTl = gsap.timeline({
        scrollTrigger: {
          trigger: heroContentRef.current,
          start: "top top",
          end: "+=100%",
          scrub: 1.5,
          pin: true,
          anticipatePin: 1,
        },
      });

      heroTl
        .to(imageRef.current, {
          scale: 1.8,
          y: 200,
          opacity: 0,
          ease: "power3.inOut",
        })
        .to(
          heroContentRef.current,
          {
            y: -200,
            opacity: 0,
            ease: "power3.inOut",
          },
          0
        );

      // Feature section animations
      // Section heading animation
      gsap.fromTo(
        sectionHeadingRef.current,
        { y: 50, opacity: 0, filter: "brightness(1)" },
        {
          y: 0,
          opacity: 1,
          filter: "brightness(1.5)",
          duration: 1.5,
          scrollTrigger: {
            trigger: sectionHeadingRef.current,
            start: "top 90%",
            end: "top 60%",
            scrub: 1.5,
            toggleActions: "play none none reverse",
          },
        }
      );

      // Feature sections animations
      features.forEach((feature, index) => {
        const video = videoRefs.current[index];
        const content = featureRefs.current[index];
        const videoContainer = video.parentElement;
        const videoMask = videoContainer.querySelector(".videoMask");
        const direction = feature.direction === "left" ? -1 : 1;

        // Create timeline for coordinated animations
        const videoTl = gsap.timeline({
          scrollTrigger: {
            trigger: videoContainer,
            start: "top bottom-=10%",
            end: "top center",
            scrub: 1.5,
            toggleActions: "play pause resume reset",
          },
        });

        // Video entrance animation
        videoTl
          .fromTo(
            video,
            { scale: 0.9, opacity: 0 },
            { scale: 1, opacity: 1, duration: 1.2 }
          )
          .fromTo(videoMask, { opacity: 1 }, { opacity: 0, duration: 1.5 }, 0);

        // Content animation
        gsap.fromTo(
          content,
          { x: 150 * direction, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            scrollTrigger: {
              trigger: content,
              start: "top 85%",
              end: "top 50%",
              scrub: 1.2,
            },
          }
        );

        // Reverse mask animation on scroll up
        gsap.fromTo(
          videoMask,
          { opacity: 0 },
          {
            opacity: 1,
            scrollTrigger: {
              trigger: videoContainer,
              start: "center center",
              end: "bottom top",
              scrub: 1,
              toggleActions: "play pause resume reset",
            },
          }
        );

        // Content slide animation
        gsap.fromTo(
          content,
          { x: 150 * direction, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            scrollTrigger: {
              trigger: content,
              start: "top 85%",
              end: "top 50%",
              scrub: 1.2,
              toggleActions: "play none none reverse",
            },
          }
        );

        // Reverse animations on scroll up
        gsap.fromTo(
          video,
          { scale: 1 },
          {
            scale: 0.9,
            opacity: 0,
            scrollTrigger: {
              trigger: video,
              start: "top center",
              end: "bottom top",
              scrub: 1,
              toggleActions: "restart pause resume reset",
            },
          }
        );
      });
    });
    return () => ctx.revert();
  }, []);

  // Updated features array for AI Image Expand
  const features = [
    {
      title: "Seamless Image Expansion",
      content:
        "Expand your images beyond their borders with AI-powered tools. Add realistic backgrounds and extend landscapes for perfect compositions.",
      video: "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      direction: "left",
    },
    {
      title: "AI-Powered Background Generation",
      content:
        "Generate realistic backgrounds to fill expanded areas. Perfect for creating panoramic views or extending social media posts.",
      video:
        "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      direction: "right",
    },
    {
      title: "4K/8K Resolution Support",
      content:
        "Expand and enhance images to 4K or 8K resolution for professional printing or digital display. Ideal for photographers and designers.",
      video: "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      direction: "left",
    },
    {
      title: "Customizable Expansion Areas",
      content:
        "Select specific areas to expand and customize the results. Perfect for creative projects and professional compositions.",
      video: "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      direction: "right",
    },
  ];

  return (
    <Box className={styles.mainContainer} ref={gradientRef}>
      {/* Added SEO Head Section */}
         <Helmet>
        <html lang="en" />
        <meta charSet="utf-8" />
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonical} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta
          property="og:image"
          content="https://cdn.photofix.com/og-image.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta
          name="twitter:image"
          content="https://cdn.photofix.com/twitter-card.jpg"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(seoData.schemaJSON)}
        </script>

        {/* Additional SEO Enhancements */}
        <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
        <meta name="robots" content="index, follow" />
        <link rel="alternate" hreflang="x-default" href={seoData.canonical} />
      </Helmet>
      <Navbar />
      <Container className={styles.contentContainer}>
        <Box className={styles.textContainer}>
          <Typography variant="h1" className={styles.heading}>
            <span
              ref={(el) => (headingRef.current[0] = el)}
              className={styles.gradientText}
            >
              AI Image Expand:
            </span>
            <br />
            <span
              ref={(el) => (headingRef.current[1] = el)}
              className={styles.strokeText}
            >
              Expand Your Images Seamlessly
            </span>
          </Typography>

          <Typography
            variant="subtitle1"
            className={styles.description}
            ref={descriptionRef} // Added ref here
          >
            Enlarge and extend your photos with AI-powered tools. Perfect for
            social media, prints, and creative projects. Try it free today!
          </Typography>

          <Box className={styles.buttonContainer}>
            <Button
              variant="contained"
              className={styles.generateButton}
              ref={buttonRef}
              onClick={()=>navigate("/dashboard/ai-expand-images")}
            >
              <span className={styles.buttonText}>Expand Photos Now</span>
              <div className={styles.buttonGlow} />
            </Button>
          </Box>
        </Box>

        <Box className={styles.imageContainer}>
          <div className={styles.imageWrapper}>
         <img
              ref={imageRef}
              src="https://cdn.web.imagine.art/imagine-frontend/assets/images/ai-image-generator-hero-image.png"
              alt="AI Art Generator Platform Creating Digital Paintings"
              title="AI Art Generator Platform Creating Digital Paintings"
              class={styles.image}
              width="1200"
              height="630"
              loading="lazy"
              decoding="async"
              aria-describedby="image-caption"
            />
            <div className={styles.imageGlow} />
            <div className={styles.imageShine} />
          </div>
        </Box>
      </Container>
      <ImageSlider />
      <ImageSlider2 />
      <Container maxWidth="xl" className={styles.featureContainer}>
        <div ref={sectionHeadingRef}>
          <h2 className={styles.sectionHeading}>
            Why Choose Our AI Image Expand?
          </h2>
          <p className={styles.sectionSubheading}>
            Professional tools for photographers, designers, and creators
          </p>
        </div>

        {features.map((feature, index) => (
          <section
            key={feature.title}
            ref={(el) => (sectionRefs.current[index] = el)}
            className={styles.featureSection}
          >
            <Grid container spacing={6} alignItems="center">
              <Grid
                item
                xs={12}
                md={6}
                order={{ xs: 2, md: index % 2 ? 2 : 1 }}
              >
                <div
                  ref={(el) => (featureRefs.current[index] = el)}
                  className={`${styles.contentWrapper} feature-content`}
                >
                  <Typography variant="h2" className={styles.featureTitle}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" className={styles.featureText}>
                    {feature.content}
                  </Typography>
                </div>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                order={{ xs: 1, md: index % 2 ? 1 : 2 }}
              >
                <div className={styles.videoContainer}>
                  <video
                    ref={(el) => (videoRefs.current[index] = el)}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className={styles.featureVideo}
                  >
                    <source src={feature.video} type="video/webm" />
                  </video>
                  <div className={styles.videoMask} />
                </div>
              </Grid>
            </Grid>
          </section>
        ))}
      </Container>
      <PricingCards />
      <DownloadSection />
      <FAQ />
      <Footer />
    </Box>
  );
};

export default AIImageExpand;
